import { useEffect, useState, useRef } from 'react';
import { Upload, Trash2, Copy, Check, Image as ImageIcon } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetMedia, adminUploadMedia, adminDeleteMedia } from '../../services/api';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminMedia() {
  const { token } = useAdminAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const load = () => {
    adminGetMedia(token)
      .then(setMedia)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleUpload = async (files) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const item = await adminUploadMedia(token, file);
        setMedia(prev => [item, ...prev]);
      } catch (err) {
        alert(`Failed to upload ${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media file?')) return;
    try {
      await adminDeleteMedia(token, id);
      setMedia(prev => prev.filter(m => m._id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const copyUrl = (item) => {
    const url = item.url.startsWith('/') ? `${BASE}${item.url}` : item.url;
    navigator.clipboard.writeText(url);
    setCopiedId(item._id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">MEDIA LIBRARY</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">{media.length} files uploaded</p>
        </div>
        <button
          data-testid="upload-media-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
        >
          <Upload size={15} />
          {uploading ? 'Uploading...' : 'Upload Files'}
        </button>
        <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={e => handleUpload(e.target.files)} />
      </div>

      {/* Drop zone */}
      <div
        data-testid="media-dropzone"
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
        className={`border-2 border-dashed p-10 text-center mb-6 transition-colors cursor-pointer ${
          dragOver ? 'border-orange-500 bg-orange-500/5' : 'border-white/10 hover:border-white/20'
        }`}
        onClick={() => fileRef.current?.click()}
      >
        <Upload size={28} className={`mx-auto mb-2 ${dragOver ? 'text-orange-500' : 'text-zinc-600'}`} />
        <p className="font-inter text-sm text-zinc-500">
          {uploading ? 'Uploading...' : 'Drag & drop files here, or click to browse'}
        </p>
        <p className="font-inter text-xs text-zinc-700 mt-1">JPG, PNG, WEBP, GIF, MP4, MOV, SVG</p>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : media.length === 0 ? (
        <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
          <ImageIcon size={40} className="text-zinc-700 mx-auto mb-3" />
          <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO MEDIA UPLOADED YET</p>
          <p className="font-inter text-sm text-zinc-600 mt-1">Upload your first image or video above</p>
        </div>
      ) : (
        <div data-testid="media-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {media.map(item => (
            <div
              key={item._id}
              data-testid={`media-item-${item._id}`}
              className="group relative bg-[#1a1a1a] border border-white/10 hover:border-orange-500/40 overflow-hidden"
            >
              {/* Preview */}
              <div className="aspect-square bg-[#0f0f0f] flex items-center justify-center overflow-hidden">
                {item.file_type === 'video' ? (
                  <video
                    src={item.url.startsWith('/') ? `${BASE}${item.url}` : item.url}
                    className="w-full h-full object-cover"
                    muted
                  />
                ) : (
                  <img
                    src={item.url.startsWith('/') ? `${BASE}${item.url}` : item.url}
                    alt={item.original_name}
                    className="w-full h-full object-contain p-1"
                    onError={e => { e.target.style.opacity = '0.2'; }}
                  />
                )}
              </div>
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  data-testid={`copy-url-${item._id}`}
                  onClick={() => copyUrl(item)}
                  className="p-2 bg-orange-500 hover:bg-orange-600 text-white transition-colors"
                  title="Copy URL"
                >
                  {copiedId === item._id ? <Check size={14} /> : <Copy size={14} />}
                </button>
                <button
                  data-testid={`delete-media-${item._id}`}
                  onClick={() => handleDelete(item._id)}
                  className="p-2 bg-red-500/80 hover:bg-red-500 text-white transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {/* File info */}
              <div className="p-2">
                <p className="font-inter text-[10px] text-zinc-500 truncate">{item.original_name}</p>
                <p className="font-inter text-[9px] text-zinc-700">{(item.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Usage tip */}
      <div className="mt-6 bg-[#1a1a1a] border border-white/10 p-4">
        <p className="font-inter text-xs text-zinc-500">
          <strong className="text-zinc-300">Tip:</strong> Click the copy icon on any image to copy its URL. Paste the URL into the Image URL field in the product editor or homepage editor.
        </p>
      </div>
    </div>
  );
}
